import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import {
  rcoHistoricalOrderValueSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SegmentNumberOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoCurrencyInput } from "@redotech/redo-web/arbiter-components/input/redo-currency-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const HistoricalOrderValueInspectorContent = memo(
  function HistoricalOrderValueInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoHistoricalOrderValueSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Condition
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          Customer's historical order value is{" "}
          {segmentNumberOperatorToText[segment.operator]}{" "}
          {moneyFormat({ amount: segment.amount, currency: segment.currency })}
        </Text>
      </Flex>
    );
  },
);

export const EditableHistoricalOrderValueInspectorContent = memo(
  function EditableHistoricalOrderValueInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoHistoricalOrderValueSegmentSchema.parse(step.segment),
      [step],
    );

    const segmentValue = useMemo(
      () => ({ amount: segment.amount, currency: segment.currency }),
      [segment.amount, segment.currency],
    );

    const handleOperatorChange = useHandler(
      (operator: SegmentNumberOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleValueChange = useHandler(
      (value?: { amount: number; currency?: Currency }) => {
        const amount = value?.amount ?? 0;
        const currency = value?.currency ?? segment.currency;
        setStep({ ...step, segment: { ...segment, amount, currency } });
      },
    );

    const selectedOperator = useMemo(
      () => operatorOptions.find((o) => o.value === segment.operator),
      [segment.operator],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          <Flex align="center" dir="row" gap="md" wrap="wrap">
            <Text fontSize="xs" fontWeight="medium">
              Historical order value is
            </Text>
            <RedoSingleSelectDropdownInput<SegmentNumberOperator>
              fitToAnchor={false}
              flexGrow={false}
              options={operatorOptions}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              placeholder="Select comparison"
              selectedItem={selectedOperator}
              size="xs"
            />
          </Flex>
          <Flex align="center" dir="row" gap="sm">
            <RedoCurrencyInput
              placeholder={0}
              setValue={handleValueChange}
              size="sm"
              value={segmentValue}
            />
          </Flex>
        </Flex>
      </>
    );
  },
);

const operatorOptions: TextRedoListItem<SegmentNumberOperator>[] = [
  {
    type: "text",
    id: SegmentNumberOperator.LESS_THAN,
    value: SegmentNumberOperator.LESS_THAN,
    text: "less than",
  },
  {
    type: "text",
    id: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    value: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    text: "less than or equal to",
  },
  {
    type: "text",
    id: SegmentNumberOperator.EQUAL,
    value: SegmentNumberOperator.EQUAL,
    text: "equal to",
  },
  {
    type: "text",
    id: SegmentNumberOperator.GREATER_THAN,
    value: SegmentNumberOperator.GREATER_THAN,
    text: "greater than",
  },
  {
    type: "text",
    id: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    value: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    text: "greater than or equal to",
  },
];

const segmentNumberOperatorToText: Record<SegmentNumberOperator, string> = {
  [SegmentNumberOperator.LESS_THAN]: "less than",
  [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: "less than or equal to",
  [SegmentNumberOperator.EQUAL]: "equal to",
  [SegmentNumberOperator.GREATER_THAN]: "greater than",
  [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: "greater than or equal to",
};
